'use client'

import { z } from 'zod'

import { parseFormattedNumber } from '@/features/order/lib/number-format-util'
import type { AccountBinding } from '@/types/dashboard'
import { TimeInForce } from '@/types/generated/common/trading'
import { IncrementalTicker } from '@/types/generated/common/types'

// Option-specific data
export const optionDataSchema = z.object({
  instrumentName: z.string(),
  askBidSide: z.enum(['ask', 'bid']),
  price: z.string(),
  ticker: z.custom<IncrementalTicker>().optional(),
})

// Unified order item schema - works for both instruments and options
export const unifiedItemSchema = z.object({
  // Unique identifier
  id: z.string(),

  // Symbol (for instruments) or instrumentName (for options)
  symbol: z.string().min(1),

  // Trading side
  side: z.enum(['buy', 'sell']),

  // Amount for this leg
  amount: z.string().optional(),

  // Denomination unit for this leg (e.g. 'USD', 'BTC', 'ETH')
  amountUnit: z.string().optional(),

  // Type discriminator
  itemType: z.enum(['instrument', 'option']),

  // Instrument-specific fields
  accountId: z.string().optional(),
  // May a row's accountId be retargeted by the AccountTab? Threaded straight through
  // from the matching SelectedInstrument/SelectedOption (see optionsToItems/
  // instrumentsToItems) — z.custom, not z.object, since RHF/zod resolvers here only
  // need the value round-tripped, not re-validated field by field.
  accountBinding: z.custom<AccountBinding>().optional(),
  baseCurrency: z.string().optional(),
  quoteCurrency: z.string().optional(),
  instrumentKind: z.string().optional(),
  lastPrice: z.string().optional(),
  exchange: z.string().optional(),
  exchangeSymbol: z.string().optional(),
  exchangeId: z.string().optional(),
  internalId: z.string().optional(),
  settlementCurrency: z.string().optional(),
  tickSize: z.string().optional(),
  minTradeAmount: z.string().optional(),
  contractSize: z.string().optional(),
  pricePrecision: z.string().optional(),
  qtyPrecision: z.string().optional(),
  expirationTsMs: z.string().optional(),
  fundingInterval: z.string().optional(),

  // Spreader-specific: which role this leg plays (set by a spread-matrix cell click, or
  // toggled per-row while in spreader mode — see isSpreaderMode in use-unified-order-form.ts).
  spreaderRole: z.enum(['quoting', 'taking']).optional(),

  // Close-position row on a venue without partial close: the size is not editable.
  lockedAmount: z.boolean().optional(),

  // Option-specific fields
  optionData: optionDataSchema.optional(),
})

// Main unified order form schema
export const unifiedOrderFormSchema = z.object({
  // Array of order items (supports single and multi-leg)
  items: z.array(unifiedItemSchema).min(1, 'At least one item is required'),

  // Global price (for limit orders)
  price: z.string().optional(),

  // TWAP-specific: duration in minutes
  twapIntervalMinutes: z.string().optional(),

  // TWAP-specific: frequency in seconds (for fixed mode)
  twapFrequencySeconds: z.string().optional(),

  // TWAP-specific: frequency min/max in seconds (for random mode)
  twapFrequencyMinSeconds: z.string().optional(),
  twapFrequencyMaxSeconds: z.string().optional(),

  // TWAP-specific: frequency mode (fixed or random)
  twapFrequencyMode: z.enum(['fixed', 'random']).default('fixed'),

  // TWAP-specific: order size (for fixed mode)
  twapOrderSize: z.string().optional(),

  // TWAP-specific: order size min/max (for random mode)
  twapOrderSizeMin: z.string().optional(),
  twapOrderSizeMax: z.string().optional(),

  // TWAP-specific: order size mode (fixed or random)
  twapOrderSizeMode: z.enum(['fixed', 'random']).default('fixed'),

  // Spreader-specific: fraction of the quoting leg worked per slice, as a PERCENT string
  // (e.g. "1" = 1%) — converted to a (0,1] fraction only in build-spreader-params.ts.
  spreaderSlicePct: z.string().optional(),
  // Spreader-specific: caps the quoting leg's own chase. '' = unbounded.
  spreaderLimitPrice: z.string().optional(),

  // Advanced TWAP-specific: total wall-clock duration, in seconds.
  advancedTwapDurationSeconds: z.string().optional(),
  // Advanced TWAP-specific: requested slice count. '' = server default (10); the
  // resolved count (post 10s-per-slice floor) may come back lower — see
  // features/advanced-twap/lib/advanced-twap-format.ts's resolveClientSlicing.
  advancedTwapTotalSlices: z.string().optional(),
  // Advanced TWAP-specific: price band. One % input drives both bounds (contract §7 —
  // there is no one-sided band), sent only when enabled.
  advancedTwapBandEnabled: z.boolean().default(false),
  advancedTwapBandPct: z.string().optional(),
  // No catchup fields here — the catchup leg (frequency + order size) is derived
  // automatically from duration/total slices/quantity at submit time, never user input.
  // See features/advanced-twap/lib/derive-catchup-params.ts.
  advancedTwapReduceOnly: z.boolean().default(false),

  // Scale-specific: the two ladder ends (absolute prices, either order — the near end
  // is derived from the side), the level count (2..50), and far ÷ near size ratio
  // ('' = 1, a flat ladder). See features/scale/lib/scale-ladder.ts.
  scaleStartPrice: z.string().optional(),
  scaleEndPrice: z.string().optional(),
  scaleOrderCount: z.string().optional(),
  scaleSizeRatio: z.string().optional(),

  // Entry condition fields
  entryConditionInstrument: z.string().optional(),
  entryConditionOperator: z
    .enum(['equal_to', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'])
    .default('equal_to'),
  entryConditionPrice: z.string().optional(),

  // Exit condition fields (TriggerCondition)
  priceAbove: z.string().optional(),
  priceBelow: z.string().optional(),

  // Limit price anchor (for relative limit orders)
  priceAnchor: z.enum(['price', 'mid', 'best_bid', 'best_ask']).default('price'),

  // Limit price offset unit (for relative limit orders)
  priceOffsetUnit: z.enum(['quote_value', 'bps', 'percent', 'ticks']).default('quote_value'),

  // Advanced: time in force (TimeInForce enum)
  timeInForce: z.nativeEnum(TimeInForce).default(TimeInForce.TIF_GTC),
  expiresValue: z.string().default('90'),
  expiresUnit: z.enum(['days', 'hours', 'minutes']).default('days'),
  reduceOnly: z.boolean().default(false),
  postOnly: z.boolean().default(false),
})

// Type exports
export type OptionData = z.infer<typeof optionDataSchema>
export type UnifiedItemValues = z.infer<typeof unifiedItemSchema>
export type UnifiedOrderFormValues = z.infer<typeof unifiedOrderFormSchema>

// Order form mode
export type UnifiedOrderMode = 'general' | 'single-leg' | 'multi-leg'

/** Single source of truth for the leg-count → mode threshold (e.g. exit-bracket
 * applicability, AI UIContext order-form-state mode) so callers can't drift apart. */
export function getUnifiedOrderMode(itemCount: number): UnifiedOrderMode {
  if (itemCount >= 2) return 'multi-leg'
  if (itemCount === 1) return 'single-leg'
  return 'general'
}
